import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
	deleteDocument,
	editDocumentName,
	uploadDocument,
} from 'feature/upload/uploadSlice';
import {
	IComplaintTrackingDetail,
	IComplaintTrackingState,
} from 'types/ComplaintTracking';
import {
	downloadComplaintTrackingThunk,
	getComplaintOutcomesThunk,
	getComplaintReasonsThunk,
	getComplaintTrackingDetailThunk,
	getComplaintTrackingThunk,
	saveComplaintThunk,
} from './complaintTrackingThunk';

const complaintDetailInitialState: IComplaintTrackingDetail = {
	complaintId: 0,
	claimUUID: '',
	source: '',
	type: 'INFORMAL',
	requestedDate: '',
	status: '',
	reason: '',
	closureDate: '',
	outcome: '',
	frlSent: undefined,
	frlSentDate: '',
	referralToInsurerDate: '',
	internalNotes: '',
	description: '',
	createdBy: '',
	attachments: [],
};

const initialState: IComplaintTrackingState = {
	rows: [],
	complaintDetail: complaintDetailInitialState,
	pages: 1,
	page: 1,
	search: false,
	triggerRefresh: false,
	complaintReasons: [],
	complaintOutcomes: [],
};

export const getComplaintTracking = createAsyncThunk(
	'complaintTracking/getComplaintTracking',
	getComplaintTrackingThunk,
);

export const getComplaintTrackingDetail = createAsyncThunk(
	'complaintTracking/getComplaintTrackingDetail',
	getComplaintTrackingDetailThunk,
);

export const downloadComplaintTracking = createAsyncThunk(
	'complaintTracking/downloadComplaintTracking',
	downloadComplaintTrackingThunk,
);

export const getComplaintReasons = createAsyncThunk(
	'complaintTracking/getComplaintReasons',
	getComplaintReasonsThunk,
);

export const getComplaintOutcomes = createAsyncThunk(
	'complaintTracking/getComplaintOutcomes',
	getComplaintOutcomesThunk,
);

export const saveComplaint = createAsyncThunk(
	'complaintTracking/saveComplaint',
	saveComplaintThunk,
);

const complaintTrackingSlice = createSlice({
	name: 'complaintTracking',
	initialState,
	reducers: {
		setComplaintPage: (state, { payload }: PayloadAction<number>) => {
			state.page = payload;
		},
		resetComplaintDetail: (state, { payload }: PayloadAction<string>) => {
			state.complaintDetail = {
				...complaintDetailInitialState,
				claimUUID: payload,
			};
		},
		setComplaintValue: (
			state,
			{
				payload,
			}: PayloadAction<{
				name: keyof IComplaintTrackingDetail;
				value: string | boolean | undefined;
			}>,
		) => {
			(state.complaintDetail[
				payload.name
			] as IComplaintTrackingDetail[keyof IComplaintTrackingDetail]) =
				payload.value;
		},
		clearComplaintAttachment: (state, { payload }) => ({
			...state,
			claimData: {
				...state.complaintDetail,
				attachments: state.complaintDetail!.attachments.filter(
					attachment => attachment.uuid !== payload.uuid,
				),
			},
		}),
	},
	extraReducers: builder => {
		builder
			.addCase(getComplaintTracking.fulfilled, (state, { payload }) => {
				state.rows = payload.complaints;
				state.pages = payload.pages;
			})
			.addCase(getComplaintTracking.rejected, state => {
				state.rows = [];
			})
			.addCase(getComplaintTrackingDetail.fulfilled, (state, { payload }) => {
				if (!payload) {
					state.complaintDetail = complaintDetailInitialState;
					return;
				}

				state.complaintDetail = payload;
				state.triggerRefresh = false;
			})
			.addCase(getComplaintTrackingDetail.rejected, state => {
				state.complaintDetail = { ...complaintDetailInitialState };
			})
			.addCase(getComplaintReasons.fulfilled, (state, { payload }) => {
				state.complaintReasons = payload;
			})
			.addCase(getComplaintReasons.rejected, state => {
				state.complaintReasons = [];
			})
			.addCase(getComplaintOutcomes.fulfilled, (state, { payload }) => {
				state.complaintOutcomes = payload;
			})
			.addCase(getComplaintOutcomes.rejected, state => {
				state.complaintOutcomes = [];
			})
			.addCase(uploadDocument.fulfilled, (state, action) => {
				const {
					meta: {
						arg: { docType },
					},
				} = action;

				const { uuid, fileName } = action.payload;

				if (!state.complaintDetail) return;

				if (docType === 'complaint') {
					state.complaintDetail.attachments = [
						...state.complaintDetail.attachments,
						{ uuid, fileName },
					];
				}
			})
			.addCase(deleteDocument.fulfilled, (state, action) => {
				const {
					meta: {
						arg: { uuid },
					},
				} = action;

				if (!state.complaintDetail) return;

				state.complaintDetail.attachments =
					state.complaintDetail.attachments?.filter(
						attachment => attachment.uuid !== uuid,
					);
			})
			.addCase(editDocumentName.fulfilled, (state, action) => {
				const {
					meta: {
						arg: { documentUuid },
					},
				} = action;

				if (!state.complaintDetail) return;

				return {
					...state,
					complaintDetail: {
						...state.complaintDetail,
						attachments: [
							...state.complaintDetail.attachments.map(el =>
								el.uuid !== documentUuid
									? el
									: {
											uuid: documentUuid,
											fileName: action.payload.fileName,
										},
							),
						],
					},
				};
			})
			.addCase(saveComplaint.fulfilled, (state, { payload }) => {
				state.complaintDetail = {
					...state.complaintDetail,
					complaintId: payload.complaintId,
				};
				state.triggerRefresh = true;
			});
	},
});

export const {
	setComplaintPage,
	setComplaintValue,
	resetComplaintDetail,
	clearComplaintAttachment,
} = complaintTrackingSlice.actions;
export default complaintTrackingSlice.reducer;
